/**
 * Browser traces that follow a screen from the click to its first data on screen.
 *
 * The Elastic agent ends a page-load or route-change transaction as soon as no request is in
 * flight. A screen asks for its data after its first render, so the transaction closed before
 * the list's requests started and they sat outside every trace. Here the transaction is held
 * open until the screen says its first data is drawn (`usePageReady`), then released.
 *
 * Every function is a no-op while the agent is off, so callers never check.
 */
import { apm } from "@elastic/apm-rum";
import type { Span, Transaction } from "@elastic/apm-rum";

/** A screen that never says it is ready (a stuck request) must not hold a transaction forever. */
export const PAGE_READY_CAP_MS = 15_000;
/** A screen that does not call `usePageReady` by now is not going to; let the agent close the
 * transaction the way it always did. */
export const PAGE_CLAIM_WINDOW_MS = 1_000;
/** The first screen waits longer: sign-in (the redirect back, the token) can keep the screen that
 * asks for data from mounting for several seconds, and a page-load that closed before it left the
 * screen's requests as loose `http-request` transactions. */
export const PAGE_LOAD_CLAIM_WINDOW_MS = 8_000;
/** Requests the first render starts (match counts, a profile) begin after the screen says it is
 * ready; wait this long so they join the transaction instead of falling outside it. */
export const PAGE_SETTLE_MS = 150;

/** What a screen holds after `claimPage()`. `ready()` may be called any number of times. */
type PageClaim = { ready: () => void };
const NO_CLAIM: PageClaim = { ready: () => {} };

type Pending = {
  tr: Transaction;
  holdTask: string;
  startedAt: number;
  claim: PageClaim | undefined;
  span: Span | undefined;
  claimTimer: ReturnType<typeof setTimeout> | undefined;
  capTimer: ReturnType<typeof setTimeout>;
};

let pending: Pending | undefined;
let bootAt: number | undefined;
let holdCount = 0;

function currentIs(tr: Transaction): boolean {
  return apm.getCurrentTransaction() === tr;
}

/** A named point on the page-load or route-change in progress, and in the browser's own
 * timeline (`performance.mark`), so DevTools shows the same names. `key` is a short snake_case
 * word: the agent rewrites `.`, `*` and `"` in it. */
export function markRum(key: string): void {
  try {
    performance.mark(`novia:${key}`);
  } catch {
    // No User Timing here (a test, an old browser): the agent's own mark below still works.
  }
  // The agent starts after the config loads, so `boot` happens before it: keep the time and put
  // it on the page-load transaction when that exists (`holdPageLoad`).
  if (key === "boot") bootAt = performance.now();
  if (!apm.isActive()) return;
  apm.getCurrentTransaction()?.mark(key);
}

/** A bar in the page-load waterfall between two `markRum` points. The agent turns the
 * browser's `measure` entries longer than 60 ms into spans. */
export function measureRum(name: string, from: string, to: string): void {
  try {
    performance.measure(name, `novia:${from}`, `novia:${to}`);
  } catch {
    // One of the marks was never made (RUM started late): no bar, nothing else lost.
  }
}

function hold(tr: Transaction, claimWindow: boolean): Pending {
  const holdTask = `novia-page-hold-${++holdCount}`;
  tr.addTask(holdTask);
  const p: Pending = {
    tr,
    holdTask,
    startedAt: performance.now(),
    claim: undefined,
    span: undefined,
    claimTimer: undefined,
    capTimer: setTimeout(() => {
      p.span?.addLabels({ page_ready: "timeout" });
      if (p.span) p.span.name = "first data rendered (gave up)";
      release(p, true);
    }, PAGE_READY_CAP_MS),
  };
  if (claimWindow) p.claimTimer = armClaimWindow(p);
  pending = p;
  return p;
}

function armClaimWindow(p: Pending): ReturnType<typeof setTimeout> {
  const window = p.tr.type === "page-load" ? PAGE_LOAD_CLAIM_WINDOW_MS : PAGE_CLAIM_WINDOW_MS;
  return setTimeout(() => {
    if (!p.claim) release(p, false);
  }, window);
}

/** `endNow`: the screen said its data is drawn (or gave up), so the transaction ends here, not
 * when the last request finishes. The agent would otherwise keep it open for every request still
 * running (a match-count call can take 4 s), and the trace would read 5 s for a 1 s screen. A
 * request still running becomes a truncated span. Without `endNow` (a screen that never says),
 * the transaction ends when its requests do. */
function release(p: Pending, endNow: boolean): void {
  clearTimeout(p.claimTimer);
  clearTimeout(p.capTimer);
  p.span?.end();
  p.span = undefined;
  p.tr.removeTask(p.holdTask);
  if (endNow) p.tr.end();
  if (pending === p) pending = undefined;
}

/** Keep the page-load transaction open from the moment the agent starts, through sign-in and the
 * first render. Call once, right after the agent starts. */
export function holdPageLoad(): void {
  if (!apm.isActive()) return;
  const tr = apm.getCurrentTransaction();
  if (tr && !pending) hold(tr, false);
  if (tr && bootAt !== undefined) {
    // `boot` came before the agent: place it on this transaction's own clock.
    const inner = tr as unknown as {
      _start?: number;
      addMarks?: (marks: { custom: Record<string, number> }) => void;
    };
    if (typeof inner._start === "number") {
      inner.addMarks?.({ custom: { boot: Math.max(0, bootAt - inner._start) } });
    }
  }
}

/**
 * A screen opened, named by its route pattern (`/candidates/:candidateId`), never by the address:
 * the address holds ids. The first screen renames the page-load transaction; later ones start a
 * `route-change` transaction.
 */
export function beginRoute(pattern: string): void {
  if (!apm.isActive()) return;
  const open = pending && currentIs(pending.tr) ? pending : undefined;
  if (open && open.tr.type === "page-load" && !open.claim) {
    // Still the first screen (a redirect such as "/" to "/candidates" lands here twice).
    open.tr.name = pattern;
    clearTimeout(open.claimTimer);
    open.claimTimer = armClaimWindow(open);
    return;
  }
  if (pending) release(pending, false);
  const tr = apm.startTransaction(pattern, "route-change", { managed: true, canReuse: true });
  if (tr) hold(tr, true);
}

/**
 * A screen takes charge of when its transaction ends. Returns a handle whose `ready()` says the
 * first data is drawn. Calling again for the same screen returns the same handle.
 */
export function claimPage(): PageClaim {
  const p = pending;
  if (!p || !currentIs(p.tr)) return NO_CLAIM;
  if (p.claim) return p.claim;
  clearTimeout(p.claimTimer);
  p.span = p.tr.startSpan("first data rendered", "app.ready", { startTime: p.startedAt });
  const claim: PageClaim = {
    ready: () => {
      if (pending !== p || !p.span) return;
      markRum("first_data_rendered");
      p.span.end();
      p.span = undefined;
      setTimeout(() => release(p, true), PAGE_SETTLE_MS);
    },
  };
  p.claim = claim;
  return claim;
}

/** Only for tests: forget any held transaction. */
export function resetPageTrace(): void {
  if (pending) release(pending, false);
  pending = undefined;
}
